// =============================================================================
// src/chrome/copilot-sidebar/index.ts
// CopilotSidebarChrome — AppModule da sidebar Notion-style do Copiloto IA.
//
// Proveniencia: orquestra o porte de unified.html (markup 125-263 + IIFE
//   2835-3107) e de assets/unified-app.js CopilotTactics (2636-2741).
//   render() devolve o markup fiel; init() liga: painel de contexto
//   (scroll-spy/swap), collapse/reopen, Ctrl+J e as táticas especificas.
//
// Contrato: AppModule (src/app/types.ts). render() = string HTML; init(ctx) =
//   pos-montagem com o container de DI (bus/commands/map).
//
// DESVIO GERAL PRESERVADO: o IIFE original consulta `.copilot-nav-item`, que
//   NAO existe no markup da sidebar — entao o nav-rail nunca liga handlers e o
//   rotulo de contexto nunca recebe o prefixo "NN ·". Replicamos exatamente.
// =============================================================================

import type { AppContext, AppModule, SectionId } from '../../app/types';
import { SECTION_INSIGHTS } from '../../data';
import { buildCopilotSidebarMarkup } from './copilot-sidebar-markup';
import { collectCopilotElementRefs } from './copilot-element-refs';
import { CopilotInsightPanelController } from './insight-panel-controller';
import { CopilotCollapseController } from './collapse-controller';
import { CopilotTacticsController } from './tactics-controller';
import { bindCopilotScrollSpy } from './scroll-spy-observer';

/** Secao inicial exibida no painel (currentSection do IIFE, 2951). */
const INITIAL_SECTION: SectionId = 'mapa-eleitoral';

/**
 * Modulo de chrome da sidebar do Copiloto IA. Emite a marcacao da sidebar e,
 *   apos montada, liga os controllers de painel/collapse/scroll-spy/táticas.
 */
export const CopilotSidebarChrome: AppModule = {
  id: 'chrome-copilot-sidebar',
  render: buildCopilotSidebarMarkup,
  init: initCopilotSidebar,
};

/**
 * Liga os controllers da sidebar apos o markup estar no DOM. Aborta em silencio
 *   se faltarem #aiCopilot/#copilotPanel (porte do guard, unified.html 2931).
 */
function initCopilotSidebar(ctx: AppContext): void {
  const refs = collectCopilotElementRefs();
  if (!refs) return;
  const reduceMotion = prefersReducedMotion();

  const panel = new CopilotInsightPanelController(refs, ctx.sectionInsights ?? SECTION_INSIGHTS, reduceMotion);
  panel.bootstrap();

  new CopilotCollapseController(refs).init();
  bindCopilotScrollSpy(refs, panel, ctx, reduceMotion);
  new CopilotTacticsController().init(ctx, INITIAL_SECTION);
}

/** True se o usuario pediu menos animacao (matchMedia, unified.html 2953). */
function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
